<script setup lang="ts">
import { useElementBounding } from '@vueuse/core'
import { z } from 'zod'

import { vClickOutside } from '~/directives/ClickOutside'

import TextInput from '~/components/inputs/text.vue'
import Loader from '~/components/shared/loader.vue'
import HeightExpand from '~/components/transitions/heightExpand.vue'
import LabelInput from '~/components/ui/LabelInput.vue'
import VText from '~/components/ui/VText.vue'

import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { debounce } from '~/utils'

import type SpotifyArtist from '~/types/spotifyArtist'

type Props = {
  currentArtists: SpotifyArtist[]
  label?: string
  labelAnnotation?: string
  maxArtists?: number
  placeholder?: string
  hasError?: boolean
  errMsg?: string
  showResultsFullscreen?: boolean
  showAlternative?: boolean
}

type Emits = {
  'show-page-filler': [payload: boolean]
  'open-fallback-modal': []
  'update:modelValue': [payload: SpotifyArtist[]]
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  labelAnnotation: '',
  maxArtists: 5,
  placeholder: '',
  hasError: false,
  errMsg: '',
  showResultsFullscreen: false,
  showAlternative: false,
})

const emit = defineEmits<Emits>()
const { t } = useI18n()
const uuid = useId()
const { coreFetch } = useProvideCoreFetch()
const fieldName = 'similarArtists'

const {
  value: updatedArtists,
  errorMessage: fieldErrorMessage,
  validate: validateField,
} = useFieldValidation<SpotifyArtist[]>(
  fieldName,
  z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        image: z.string(),
        popularity: z.number(),
        link: z.string().optional(),
      }),
    )
    .nonempty(t('influencer.profile.slug.edit.spotifyArtistsSelectionTooSmall'))
    .max(props.maxArtists, props.errMsg),
  { initialValue: [...props.currentArtists] },
)

const searchInputSFC = ref<InstanceType<typeof TextInput> | null>(null)
const askingSpotify = ref(false)
const focus = ref(false)
const hasScrolled = ref(false)
const isTyping = ref(false)
let searchTo = null as null | number
// sourc =: null as null | CancelTokenSource
// TOD =: check this replacement of "source" for cancelling requests
let abortController = null as null | AbortController
const apiResults = ref<SpotifyArtist[]>([])
const search = ref('')
const total = ref(0)
const limit = ref(20)
const offset = ref(0)

const hasNextPage = computed(() => offset.value < total.value - offset.value)
const hasReachedMax = computed(
  () => updatedArtists.value.length >= props.maxArtists,
)
const showResultsList = computed(
  () => focus.value && !!search.value.length && !hasReachedMax.value,
)
const userCantFindArtist = computed(
  () =>
    (props.showAlternative && hasScrolled.value) ||
    !apiResults.value.length ||
    !hasNextPage.value,
)

const openResults = debounce(function (this: any) {
  focus.value = true
  if (props.showResultsFullscreen) {
    nextTick(() => {
      emit('show-page-filler', true)
      scrollToInput()
    })
  }
}, 300)

const closeResults = debounce(function (this: any) {
  focus.value = false
  if (props.showResultsFullscreen) {
    nextTick(() => {
      emit('show-page-filler', false)
      scrollTo({ top: 0, behavior: 'smooth' })
    })
  }
}, 300)

async function toggleElement(element: SpotifyArtist) {
  const index = updatedArtists.value.findIndex((e) => e.id === element.id)

  if (index !== -1) updatedArtists.value.splice(index, 1)
  else updatedArtists.value.push(element)
  await validateField()
  emit('update:modelValue', updatedArtists.value)

  closeResults()
  search.value = ''
  total.value = 0
  limit.value = 20
  offset.value = 0
}

function execSearch(appendResults = false): void {
  if (abortController !== null) {
    askingSpotify.value = false
    abortController.abort()
    abortController = null
    execSearch(appendResults)
  }
  if (!askingSpotify.value && search.value.length) {
    abortController = new AbortController()
    const signal = abortController.signal
    askingSpotify.value = true
    coreFetch
      .$get(`/distantapi/spotify/search/`, {
        params: {
          q: search.value,
          limit: limit.value,
          offset: offset.value,
          full: true,
        },
        signal, // this is the abort signal
      })
      .then(
        ({ results, total }: { results: SpotifyArtist[]; total: number }) => {
          total =
            appendResults && !results.length ? apiResults.value.length : total
          apiResults.value = appendResults
            ? [...apiResults.value, ...results]
            : results
        },
      )
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        askingSpotify.value = false
        if (props.showResultsFullscreen) {
          emit('show-page-filler', true)
          scrollToInput()
        }
      })
  }
}

function handleScrolledToBottom(event: Event): void {
  if (!event.currentTarget) return

  const { scrollHeight, offsetHeight, scrollTop } =
    event.currentTarget as HTMLElement

  const bottomScroll = scrollHeight - offsetHeight - 1 // small pixel offset due to added element

  if (!hasScrolled.value && scrollTop >= 60) hasScrolled.value = true

  const scrolledToBottom = Math.round(scrollTop) >= bottomScroll
  if (scrolledToBottom && hasNextPage.value)
    handleSearchTo(50, apiResults.value.length, true)
}

function handleSearchTo(
  searchLimit: number,
  searchOffset: number,
  appendResults = false,
): void {
  if (searchTo !== null) window.clearTimeout(searchTo)

  isTyping.value = true
  searchTo = window.setTimeout(() => {
    isTyping.value = false
    limit.value = searchLimit
    offset.value = searchOffset
    execSearch(appendResults)
  }, 350)
}

function scrollToInput() {
  nextTick(() => {
    const { top: textInputElementTop } = useElementBounding(searchInputSFC)
    const scrollY = window.scrollY
    if (textInputElementTop.value)
      scrollTo({ top: textInputElementTop.value + scrollY, behavior: 'smooth' })
  })
}

watch(search, () => {
  offset.value = 0
  limit.value = 20
  hasScrolled.value = false
  handleSearchTo(limit.value, offset.value)
})
</script>

<template>
  <div :name="fieldName">
    <LabelInput
      :label-annotation="labelAnnotation"
      :html-for="uuid ? uuid : ''"
    >
      <template #label>
        <VText cfg="sans/16/medium">
          {{ label }}
        </VText>
      </template>
    </LabelInput>
    <div
      v-click-outside="closeResults"
      class="tw-relative tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-3 tw-border tw-bg-white tw-px-4 tw-py-[9px] md:tw-gap-4"
      :class="{ focus, 'has-error': hasError }"
    >
      <div
        v-for="artist in updatedArtists"
        :key="artist.id"
        class="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-bg-blue-500 tw-px-4 tw-py-2 tw-text-white"
        @click="toggleElement(artist)"
      >
        <VText
          cfg="sans/14/medium"
          class="tw-mr-2 tw-inline-block tw-text-white"
          >{{ artist.name }}</VText
        >
        <i class="fas fa-times tw-text-sm" />
      </div>
      <TextInput
        v-show="!hasReachedMax"
        :id="uuid"
        ref="searchInputSFC"
        v-model="search"
        :show-input-border="false"
        no-padding
        validated-picto-class=""
        :placeholder="placeholder"
        class="tw-my-[-9px] tw-flex-grow"
        :disabled="hasReachedMax"
        p-validity
        @focus="openResults"
      />
      <transition name="fade">
        <div
          v-if="showResultsList"
          key="results"
          class="tw-absolute tw-left-0 tw-top-[calc(100%+1px)] tw-z-[10050] tw-mb-12 tw-w-full tw-rounded tw-border tw-border-solid tw-border-gray-300 tw-bg-white"
        >
          <div>
            <div
              class="results-full tw-min-h-[80px] tw-overflow-y-scroll tw-p-2 sm:tw-max-h-[calc(100vh-530px)]"
              :aria-expanded="!!apiResults.length"
              @scroll="handleScrolledToBottom"
            >
              <div
                v-for="element in apiResults"
                :key="element.id"
                class="tw-relative tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-start tw-bg-white tw-px-4 tw-py-1 tw-transition-colors tw-duration-150 tw-ease-in-out hover:tw-bg-orange-100"
                @click.stop="toggleElement(element)"
              >
                <img
                  :src="element.image"
                  class="tw-mr-1 tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-full tw-border tw-border-solid tw-border-gray-300 tw-object-contain tw-object-center"
                  alt=""
                />
                <div class="tw-w-9/12 tw-shrink-0">
                  {{ element.name }}
                </div>
                <i
                  v-if="updatedArtists.some((e) => e.id === element.id)"
                  class="fas fa-check tw-text-orange-500"
                ></i>
              </div>
              <Loader
                v-if="userCantFindArtist && askingSpotify"
                class="tw-bg-white tw-bg-opacity-5"
              />

              <div v-if="userCantFindArtist" class="tw-h-24 sm:tw-h-20" />
              <div
                v-if="!apiResults.length"
                class="tw-flex tw-h-15 tw-items-center tw-justify-center tw-text-gray-400"
              >
                {{ $t(`influencer.profile.slug.edit.spotifyArtists03`) }}
              </div>
            </div>
          </div>

          <div
            v-if="(isTyping || askingSpotify) && !hasScrolled"
            class="tw-h-full"
          >
            <Loader class="tw-bg-white" />
          </div>
        </div>
      </transition>
    </div>
    <HeightExpand>
      <div v-if="fieldErrorMessage" class="errorHolder tw-mt-2xs">
        <VText cfg="sans/14/medium" class="tw-text-error">
          {{ fieldErrorMessage }}
        </VText>
      </div>
    </HeightExpand>
  </div>
</template>

<style lang="scss" scoped>
.expanded > .inputOuterWrap {
  max-width: unset;
}

.focus {
  @apply tw-border-gray-500 tw-transition-colors tw-duration-100 tw-ease-in-out;

  box-shadow: unset !important;
}

.has-error {
  @apply tw-border-error;
}

.results-full {
  // full height - (footer height + padding + mobile keyboard height or 0px when it's not out)
  height: calc(100vh - 80px - 32px - env(keyboard-inset-height, 0px));
}
</style>
